/* eslint-disable no-unused-expressions */
import { useState, useCallback, useEffect } from 'react';
import isEmpty from 'lodash/isEmpty';

import { CoreSubject } from '../../types';
import { findSubject } from '../../utils';

const getTreeInitialState = (subjects?: CoreSubject[], subjectId?: string, result: Record<string, boolean> = {}): Record<string, boolean> => {
  if (!subjectId || !subjects) return result;

  const subject = findSubject(subjects, subjectId);

  if (!subject || !subject.parentSubject) return result;

  const { parentSubject } = subject;
  return getTreeInitialState(subjects, parentSubject, {
    ...result,
    [parentSubject]: true,
  });
};

interface SubjectsTreeHookInput {
  subjects?: CoreSubject[];
  selectedSubjectId?: string;

  onSelectLeafSubject?: (subject: CoreSubject) => void;
}

interface SubjectsTreeHookOutput {
  onToggleSubject: (subject: CoreSubject) => void;
  isSubjectOpen: (subject: CoreSubject) => boolean;
  isSubjectSelected: (subject: CoreSubject) => boolean;
}

export default function useSubjectsTree(input: SubjectsTreeHookInput): SubjectsTreeHookOutput {
  const { subjects, selectedSubjectId, onSelectLeafSubject } = input;

  const [subjectsData, setSubjectsData] = useState<Record<string, boolean>>(getTreeInitialState(subjects, selectedSubjectId));

  useEffect(() => {
    if (!selectedSubjectId || !subjects || !subjects.length) return;
    setSubjectsData((state) => (isEmpty(state) ? getTreeInitialState(subjects, selectedSubjectId) : state));
  }, [subjects, selectedSubjectId]);

  const hasSubSubjects = useCallback((subject: CoreSubject) => {
    return subject.subjects && subject.subjects.length > 0;
  }, []);

  const handleToggleSubject = useCallback(
    (subject: CoreSubject) => {
      if (!hasSubSubjects(subject)) {
        onSelectLeafSubject?.(subject);
      }
      const { id } = subject;
      setSubjectsData((state) => ({
        ...state,
        [id]: !state[id],
      }));
    },
    [hasSubSubjects, onSelectLeafSubject]
  );

  const isSubjectSelected = useCallback(
    (subject: CoreSubject) => {
      if (!subject) return false;
      return selectedSubjectId === subject.id;
    },
    [selectedSubjectId]
  );

  const isSubjectOpen = useCallback(
    (subject: CoreSubject) => {
      if (!subject) return false;
      return !!subjectsData[subject.id];
    },
    [subjectsData]
  );

  return {
    onToggleSubject: handleToggleSubject,
    isSubjectOpen,
    isSubjectSelected,
  };
}
